import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/Common";
import { toast } from "sonner";
import { Camera, CameraOff, Search, PackageCheck, PackageOpen, AlertTriangle, Clock, Printer } from "lucide-react";
import LuggageReceipt from "@/components/luggage/LuggageReceipt";

const STATUS_TONE = { free: "emerald", occupied: "amber", lost: "red", retired: "slate" };

const hoursSince = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 36e5));
};

/**
 * Scan & Serve — camera QR scanning (tablet / phone / laptop) with a manual
 * code fallback. Resolves the card to its locker, then offers Check-in
 * (deposit) or Check-out (release) depending on the locker state.
 */
export default function LuggageScan({ onChanged }) {
  const [manual, setManual] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null); // {locker, session}
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", items_description: "" });
  const [receiptOpen, setReceiptOpen] = useState(false);
  const scannerRef = useRef(null);

  const stopCamera = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      }
    } catch { /* already stopped */ }
    scannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => () => { stopCamera(); }, []);

  const lookup = async (code) => {
    if (!code?.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/luggage/scan", { code: code.trim() });
      setResult(data);
      setForm({ customer_name: "", customer_phone: "", items_description: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startCamera = async () => {
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("luggage-qr-reader");
      scannerRef.current = scanner;
      setScanning(true);
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 230, height: 230 } },
        async (text) => { await stopCamera(); lookup(text); },
        () => {},
      );
    } catch (e) {
      await stopCamera();
      toast.error("Camera unavailable — type the code under the QR instead.");
    }
  };

  const checkIn = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/luggage/check-in", {
        locker_id: result.locker.id, ...form,
      });
      setResult(data);
      toast.success(`Luggage stored in ${data.locker.locker_no}`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const checkOut = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/luggage/check-out", { locker_id: result.locker.id });
      setResult(data);
      toast.success(`${data.locker.locker_no} released — hand the luggage back`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const locker = result?.locker;
  const session = result?.session;
  const isOpen = session && session.status === "open";

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      {/* Left: scanner + manual entry */}
      <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="lug-scan-panel">
        <div className="text-[10px] uppercase tracking-[0.14em] text-gray-500 font-semibold mb-3">
          Scan a card
        </div>
        <div id="luggage-qr-reader"
          className={`rounded-lg overflow-hidden bg-gray-900/90 ${scanning ? "min-h-[260px]" : "h-0"}`} />
        {!scanning ? (
          <Button onClick={startCamera} className="w-full h-11 bg-emerald-900 hover:bg-emerald-800"
            data-testid="lug-start-camera">
            <Camera size={15} className="mr-2" /> Start camera scan
          </Button>
        ) : (
          <Button onClick={stopCamera} variant="outline" className="w-full h-11 mt-3"
            data-testid="lug-stop-camera">
            <CameraOff size={15} className="mr-2" /> Stop camera
          </Button>
        )}
        <div className="flex items-center gap-2 my-4">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-[10px] uppercase tracking-widest text-gray-400">or type the code</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); lookup(manual); }}>
          <Input value={manual} onChange={(e) => setManual(e.target.value)}
            placeholder="Code printed under the QR (ABRLG:…)"
            className="h-10 font-mono text-sm" data-testid="lug-manual-code-input" />
          <Button type="submit" disabled={busy || !manual.trim()} className="h-10 bg-emerald-900 hover:bg-emerald-800"
            data-testid="lug-lookup-button">
            <Search size={15} />
          </Button>
        </form>
      </div>

      {/* Right: result + actions */}
      <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="lug-result-panel">
        {!locker ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-14 text-gray-400">
            <PackageOpen size={34} strokeWidth={1.5} />
            <div className="text-sm mt-3">Scan or enter a card code to see its locker</div>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-gray-500 font-semibold">Locker number</div>
                <div className="font-display text-5xl font-extrabold text-emerald-900 mt-1"
                  data-testid="lug-locker-number">{locker.locker_no}</div>
                <div className="text-xs text-gray-500 mt-1">{locker.branch}</div>
              </div>
              <Badge tone={STATUS_TONE[locker.status] || "slate"}>{locker.status}</Badge>
            </div>

            {locker.status === "free" && (
              <div className="mt-5 space-y-3" data-testid="lug-checkin-form">
                <div className="text-[10px] uppercase tracking-[0.14em] text-gray-500 font-semibold">
                  Deposit luggage (all fields optional)
                </div>
                <Input placeholder="Customer name" value={form.customer_name}
                  onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))}
                  className="h-10" data-testid="lug-customer-name" />
                <Input placeholder="Customer phone" value={form.customer_phone}
                  onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))}
                  className="h-10" data-testid="lug-customer-phone" />
                <Textarea placeholder="Items description e.g. black backpack + shopping bag"
                  value={form.items_description} rows={2}
                  onChange={(e) => setForm((f) => ({ ...f, items_description: e.target.value }))}
                  data-testid="lug-items-desc" />
                <Button onClick={checkIn} disabled={busy}
                  className="w-full h-11 bg-emerald-900 hover:bg-emerald-800" data-testid="lug-checkin-button">
                  <PackageCheck size={15} className="mr-2" />
                  {busy ? "Saving…" : `Store luggage in ${locker.locker_no}`}
                </Button>
              </div>
            )}

            {locker.status === "occupied" && (
              <div className="mt-5" data-testid="lug-checkout-panel">
                {session?.is_overdue && (
                  <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 mb-3 text-sm text-red-800"
                    data-testid="lug-overdue-warning">
                    <Clock size={15} className="shrink-0" />
                    <span><strong>Overdue</strong> — stored {hoursSince(session?.checked_in_at)}h ago. Follow up with the customer.</span>
                  </div>
                )}
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-1">
                  <div><span className="font-semibold">Customer:</span> {session?.customer_name || "—"}
                    {session?.customer_phone ? ` · ${session.customer_phone}` : ""}</div>
                  <div><span className="font-semibold">Items:</span> {session?.items_description || "—"}</div>
                  <div><span className="font-semibold">Stored:</span> {(session?.checked_in_at || "").slice(0, 16).replace("T", " ")}
                    {session?.checked_in_by_name ? ` by ${session.checked_in_by_name}` : ""}</div>
                </div>
                <Button onClick={checkOut} disabled={busy}
                  className="w-full h-12 mt-4 bg-amber-500 hover:bg-amber-400 text-emerald-950 font-bold"
                  data-testid="lug-checkout-button">
                  <PackageOpen size={16} className="mr-2" />
                  {busy ? "Releasing…" : `Release luggage from ${locker.locker_no}`}
                </Button>
                <Button onClick={() => setReceiptOpen(true)} variant="outline"
                  className="w-full h-10 mt-2" data-testid="lug-receipt-button">
                  <Printer size={14} className="mr-2" /> Print deposit receipt
                </Button>
              </div>
            )}

            {(locker.status === "lost" || locker.status === "retired") && (
              <div className="mt-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                data-testid="lug-blocked-note">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                This card is marked <strong className="mx-1">{locker.status}</strong> — do not accept it.
                A manager can restore it from the Lockers tab.
              </div>
            )}
          </div>
        )}
      </div>

      {receiptOpen && locker && (
        <LuggageReceipt locker={locker} session={session} onClose={() => setReceiptOpen(false)} />
      )}
    </div>
  );
}
